/**
 * ConnectedGeomap -- wires URL params and Apollo queries to the library's
 * pure GeomapRenderer component.
 *
 * This follows the "connected wrapper" pattern established by ConnectedTreemap.
 * The existing chart code in src/visualization/charts/geomap/ continues to
 * work unchanged. New pages or apps can use this wrapper (or compose their
 * own) to adopt the library components.
 *
 * Data flow:
 *   URL params (usePageQueryParams)
 *     -> Apollo query (useQuery)
 *       -> data filtering (exclude "other" / undeclared)
 *         -> GeomapRenderer (pure, from @atlas-int/viz)
 *           -> Chart (mapbox)
 *           -> ColorLegend
 *           -> TooltipContent
 */

import {
  useMemo,
  useRef,
  memo,
  useCallback,
  type ComponentProps,
  type MutableRefObject,
} from "react";
import type { Feature, Geometry } from "geojson";
import { useQuery } from "@apollo/client/react";
import { useGeomapParams } from "../paramSchemas";
import {
  ProductLevel,
  ServicesClass,
  type ProductClass,
  type TradeDirection,
} from "@atlas-int/data/types";
import {
  getDataTradeDirection,
  computeTotalSumByTradeFlow,
  convertProductStringToNumberId,
  otherGroupId,
  undeclaredCountryId,
  unidentifiedServicesCountryId,
  allProductsDatum,
  UIView,
  useLocationsMetadata,
} from "@atlas-int/data";
import {
  determineEndpointFacet,
  mapQueryArgumentsToAPIEndpointInputs,
} from "@atlas-int/data";
import type { ExploreDataQueryVariables } from "@atlas-int/data";
import {
  getReferenceLocation,
  GeomapLegend as ColorLegend,
  GeomapChart as Chart,
} from "@atlas-int/viz/geomap";
import { TooltipContent } from "../components/tooltip/TooltipContent";
import { VisualizationContentContainer } from "../VizGrid";
import { zIndex } from "@atlas-int/tokens";
import {
  useVisualizationState,
  LegendRow,
  VisualizationContent,
  GraphNoticeType,
  TooltipStateIsolationProvider,
  TooltipStateBridge,
  TooltipPortal,
  useTooltipState,
  useTooltipDispatchRef,
  useIsolatedTooltipPayload,
  type TooltipDatum,
  type TooltipDispatchMethods,
} from "@atlas-int/viz/shared";
import VisualizationLayout from "../components/VisualizationLayout";
interface GeomapTradeDataItem {
  partnerCountryId: string;
  productId?: string;
  exportValue?: number;
  importValue?: number;
  [key: string]: unknown;
}

interface GeomapQueryResponse {
  data: GeomapTradeDataItem[];
}

type GeomapFeature = Feature<Geometry, Record<string, unknown>> | undefined;

type GeomapTooltipRendererProps = {
  view: UIView;
  totalValue: number | undefined;
  tradeDirection: TradeDirection;
  productClass: ProductClass;
};

const createGeomapTooltipDatum = (
  datum: GeomapFeature,
): TooltipDatum | undefined => {
  if (!datum) return undefined;

  const props = datum.properties || {};
  return {
    kind: "location" as const,
    countryId: props.countryId as string | undefined,
    nameEn: (props.nameEn || props.name) as string | undefined,
    exportValue: props.exportValue as number | undefined,
    importValue: props.importValue as number | undefined,
    properties: props,
  } as TooltipDatum;
};

const GeomapTooltipRenderer = memo(function GeomapTooltipRenderer({
  datum,
  fillColor,
  view,
  totalValue,
  tradeDirection,
  productClass,
}: GeomapTooltipRendererProps & {
  datum: TooltipDatum;
  fillColor?: string;
}) {
  return (
    <TooltipContent
      datum={datum}
      view={view}
      totalValue={totalValue}
      tradeDirection={tradeDirection}
      productClass={productClass}
      fillColor={fillColor}
    />
  );
});

const usePinnedGeomapTooltip = (selectionKey: string) => {
  const { tooltipState, showTooltip, hideTooltip, fixTooltip, clearFixed } =
    useTooltipState(undefined, selectionKey);
  const pinnedDispatchRef = useRef<TooltipDispatchMethods<TooltipDatum>>({
    showTooltip,
    hideTooltip,
    fixTooltip,
    clearFixed,
  });
  pinnedDispatchRef.current = {
    showTooltip,
    hideTooltip,
    fixTooltip,
    clearFixed,
  };

  const pinnedTooltipDismissedRef = useRef(false);
  const previousSelectionKeyRef = useRef(selectionKey);
  if (previousSelectionKeyRef.current !== selectionKey) {
    previousSelectionKeyRef.current = selectionKey;
    pinnedTooltipDismissedRef.current = false;
  }

  const dismissPinnedTooltip = useCallback(() => {
    pinnedTooltipDismissedRef.current = true;
    clearFixed();
  }, [clearFixed]);

  return {
    pinnedTooltipState: tooltipState,
    pinnedDispatchRef,
    pinnedTooltipDismissedRef,
    dismissPinnedTooltip,
  };
};

type GeomapChartProps = {
  inputData: ComponentProps<typeof Chart>["inputData"];
  queryVariables: ComponentProps<typeof Chart>["queryVariables"];
  tradeDirection: TradeDirection;
  tradeFlow: ComponentProps<typeof Chart>["tradeFlow"];
  loading: boolean;
  hasData: boolean;
  locationMetadataState: ComponentProps<typeof Chart>["locationMetadataState"];
  /** Stable dispatch ref for the pinned tooltip — separate from the hover context. */
  pinnedDispatchRef: MutableRefObject<TooltipDispatchMethods<TooltipDatum>>;
  /** Ref set to `true` when the user closes the pinned tooltip; reset when country changes. */
  pinnedTooltipDismissedRef: MutableRefObject<boolean>;
};

/**
 * Memoized map: hover uses `useTooltipDispatchRef` so payload updates do not
 * rerender this subtree (only `TooltipStateBridge` subscribes).
 */
const GeomapChartMemo = memo(function GeomapChartMemo({
  inputData,
  queryVariables,
  tradeDirection,
  tradeFlow,
  loading,
  hasData,
  locationMetadataState,
  pinnedDispatchRef,
  pinnedTooltipDismissedRef,
}: GeomapChartProps) {
  const dispatchRef = useTooltipDispatchRef(); // hover-only dispatch
  const onHover = useCallback(
    (datum: GeomapFeature) => {
      const tooltipDatum = createGeomapTooltipDatum(datum);
      if (!tooltipDatum) {
        dispatchRef.current.hideTooltip();
        return;
      }
      dispatchRef.current.showTooltip(tooltipDatum);
    },
    [dispatchRef],
  );

  const onPinnedHover = useCallback(
    (datum: GeomapFeature, coords?: [number, number]) => {
      const tooltipDatum = createGeomapTooltipDatum(datum);
      if (!tooltipDatum || !coords) {
        pinnedDispatchRef.current.clearFixed();
        return;
      }
      pinnedDispatchRef.current.fixTooltip(tooltipDatum, coords);
    },
    [pinnedDispatchRef],
  );

  if (!hasData && !loading) return null;
  return (
    <Chart
      handleTooltip={onHover}
      handlePinnedTooltip={onPinnedHover}
      inputData={inputData}
      queryVariables={queryVariables}
      tradeDirection={tradeDirection}
      tradeFlow={tradeFlow}
      isLoading={loading}
      locationMetadataState={locationMetadataState}
      pinnedTooltipDismissedRef={pinnedTooltipDismissedRef}
    />
  );
});

const GeomapTooltipContent = memo(function GeomapTooltipContent({
  view,
  totalValue,
  tradeDirection,
  productClass,
}: GeomapTooltipRendererProps) {
  const tooltipState = useIsolatedTooltipPayload();
  if (!tooltipState?.contentData) return null;
  return (
    <GeomapTooltipRenderer
      datum={tooltipState.contentData}
      view={view}
      totalValue={totalValue}
      tradeDirection={tradeDirection}
      productClass={productClass}
      fillColor={tooltipState.fillColor}
    />
  );
});

const GeomapLegendContent = memo(function GeomapLegendContent({
  exporter,
  productId,
  data,
  location,
  tradeFlow,
  tradeDirection,
}: {
  exporter: string | undefined;
  productId: string | undefined;
  data: GeomapTradeDataItem[];
  location: ReturnType<typeof getReferenceLocation>;
  tradeFlow: ComponentProps<typeof Chart>["tradeFlow"];
  tradeDirection: TradeDirection;
}) {
  if (!exporter && !productId) return null;
  return (
    <ColorLegend
      data={data}
      location={location}
      tradeFlow={tradeFlow}
      tradeDirection={tradeDirection}
    />
  );
});

/**
 * ConnectedGeomap reads all settings from URL query params, fetches data
 * via Apollo, transforms it, and renders via GeomapRenderer.
 */
const ConnectedGeomap: React.FC = () => {
  // === URL PARAMS ===
  const [
    {
      exporter,
      importer,
      product: productId,
      view,
      productClass,
      year,
      tradeFlow,
      tradeDirection,
    },
  ] = useGeomapParams();

  // === METADATA ===
  const locationsMetadataState = useLocationsMetadata();

  // === STATE ===
  const geomapElement = useRef<HTMLDivElement | null>(null);

  // === COMPUTED VALUES ===
  const dataTradeDirection = useMemo(() => {
    return getDataTradeDirection({
      tradeDirection,
      exporter,
      importer,
      view,
    });
  }, [tradeDirection, exporter, importer, view]);

  const referenceLocation = useMemo(() => {
    return getReferenceLocation({ exporter, importer });
  }, [exporter, importer]);

  const narrowedGeomapInputVariables = useMemo(() => {
    return mapQueryArgumentsToAPIEndpointInputs({
      exporter,
      importer,
      view,
    });
  }, [exporter, importer, view]);

  const productLevel = useMemo(() => {
    if (view === UIView.Markets) {
      if (productId === allProductsDatum.productId) {
        return ProductLevel.ProductSection;
      }
      return undefined;
    }
  }, [view, productId]);

  const geoMapInputVariables: ExploreDataQueryVariables = useMemo(() => {
    return {
      productClass: productClass,
      yearMax: year ?? undefined,
      yearMin: year ?? undefined,
      productLevel,
      servicesClass: ServicesClass.unilateral,
      productId:
        productId === allProductsDatum.productId
          ? undefined
          : convertProductStringToNumberId(productId),
      ...narrowedGeomapInputVariables,
    };
  }, [
    productClass,
    year,
    productLevel,
    productId,
    narrowedGeomapInputVariables,
  ]);

  const { queryToUse, queryIsInvalid } = useMemo(() => {
    return determineEndpointFacet({
      view,
      variables: geoMapInputVariables,
    });
  }, [view, geoMapInputVariables]);

  // === DATA FETCHING ===
  const {
    loading: queryLoading,
    error,
    previousData,
    data: successResponse,
  } = useQuery<GeomapQueryResponse>(queryToUse, {
    variables: geoMapInputVariables,
    skip: queryIsInvalid,
  });

  // Include metadata loading in overall loading state so the spinner stays
  // visible until the chart has everything it needs to render.
  const metadataLoading = locationsMetadataState.status !== "success";
  const loading = queryLoading || metadataLoading;

  const { data: oldData = [] } =
    (previousData as GeomapQueryResponse | undefined) || {};
  const { data: currentData = [] } =
    (successResponse as GeomapQueryResponse | undefined) || {};
  const data = loading ? oldData : currentData;

  const filteredData = useMemo(() => {
    const excludedIds = [
      otherGroupId,
      undeclaredCountryId,
      unidentifiedServicesCountryId,
    ];
    return data.filter((item) => !excludedIds.includes(item.partnerCountryId));
  }, [data]);

  // Undeclared (country-999) and Other (group-997) are not geographic
  // entities, so the map can't render them — show a notice instead.
  const isNonGeographicEntity = useMemo(() => {
    const nonGeographicIds = ["country-999", "group-997"];
    return (
      (!!exporter && nonGeographicIds.includes(exporter)) ||
      (!!importer && nonGeographicIds.includes(importer))
    );
  }, [exporter, importer]);

  // === DERIVED VALUES ===
  const totalValue = useMemo(() => {
    return (
      computeTotalSumByTradeFlow({
        data: filteredData,
        tradeFlow: tradeFlow,
        tradeDirection: dataTradeDirection,
      }) ?? 0
    );
  }, [filteredData, tradeFlow, dataTradeDirection]);

  const queryVariables = useMemo(
    () => ({ exporter, importer, productId, year, view }),
    [exporter, importer, productId, year, view],
  );

  // === VISUALIZATION STATE ===
  const vizState = useVisualizationState({
    loading,
    error,
    data: successResponse as GeomapQueryResponse | undefined,
    previousData: undefined,
    totalValue,
  });

  // === PINNED TOOLTIP (small countries) ===
  // Managed independently of the hover TooltipStateBridge so both can be
  // visible simultaneously and clicking other countries does not dismiss the pin.
  const selectionKey = `${exporter}|${importer}`;
  const {
    pinnedTooltipState,
    pinnedDispatchRef,
    pinnedTooltipDismissedRef,
    dismissPinnedTooltip,
  } = usePinnedGeomapTooltip(selectionKey);

  // === RENDER ===
  return (
    <VisualizationLayout
      totalValue={totalValue || undefined}
      findInVizOptions={[]}
      highlightedItem={undefined}
      onHighlight={() => {}}
    >
      <TooltipStateIsolationProvider dataVersion={successResponse}>
        <TooltipStateBridge
          cursor="default"
          overrideStyles={true}
          tooltipContent={
            <GeomapTooltipContent
              view={view}
              totalValue={totalValue}
              tradeDirection={tradeDirection}
              productClass={productClass}
            />
          }
          portalContainerId="overlayPortalContainerId"
        >
          <VisualizationContentContainer ref={geomapElement}>
            <VisualizationContent
              hasError={!isNonGeographicEntity && vizState.hasError}
              errorNoticeType={vizState.errorNoticeType}
              hasNoData={isNonGeographicEntity || vizState.hasNoData}
              hasZeroTrade={!isNonGeographicEntity && vizState.hasZeroTrade}
              loading={!isNonGeographicEntity && loading}
              noticeType={
                isNonGeographicEntity
                  ? GraphNoticeType.RegionNotDisplayed
                  : undefined
              }
            >
              <GeomapChartMemo
                inputData={filteredData}
                queryVariables={queryVariables}
                tradeDirection={tradeDirection}
                tradeFlow={tradeFlow}
                loading={loading}
                hasData={vizState.hasData}
                locationMetadataState={locationsMetadataState}
                pinnedDispatchRef={pinnedDispatchRef}
                pinnedTooltipDismissedRef={pinnedTooltipDismissedRef}
              />
            </VisualizationContent>
          </VisualizationContentContainer>
        </TooltipStateBridge>
      </TooltipStateIsolationProvider>
      {/* Pinned tooltip for small countries — rendered outside the hover bridge so
          both tooltips can be visible simultaneously and the pin has no
          click-outside-dismiss behaviour. */}
      {vizState.hasData &&
        pinnedTooltipState?.contentData &&
        pinnedTooltipState.isFixedCoords && (
          <TooltipPortal
            explanation={
              <GeomapTooltipRenderer
                datum={pinnedTooltipState.contentData}
                view={view}
                totalValue={totalValue}
                tradeDirection={tradeDirection}
                productClass={productClass}
                fillColor={pinnedTooltipState.fillColor}
              />
            }
            coords={{
              left: pinnedTooltipState.isFixedCoords[0],
              top: pinnedTooltipState.isFixedCoords[1],
            }}
            overrideStyles={true}
            snapToPosition={true}
            snapDirection="above"
            zIndex={zIndex.tooltip}
            isFixed={true}
            disableClickOutsideDismiss={true}
            onClose={dismissPinnedTooltip}
          />
        )}
      <LegendRow>
        <GeomapLegendContent
          exporter={exporter}
          productId={productId}
          data={filteredData}
          location={referenceLocation}
          tradeFlow={tradeFlow}
          tradeDirection={dataTradeDirection}
        />
      </LegendRow>
    </VisualizationLayout>
  );
};

export default memo(ConnectedGeomap);
